// Filters and data stickers for photo check-ins and stories, shared by web and native.

export type PhotoFilterId =
  | 'original'
  | 'warm'
  | 'cool'
  | 'mono'
  | 'vivid'
  | 'fade'
  | 'film'
  | 'dusk'

export type Overlay =
  | { kind: 'fill'; color: string; opacity: number }
  | { kind: 'linear'; from: string; to: string; opacity: number }
  | { kind: 'vignette'; color: string; opacity: number }

/** A 4×5 colour matrix in feColorMatrix order, channels and offsets in 0–1. */
export type ColorMatrix = readonly number[]

export type PhotoFilter = {
  id: PhotoFilterId
  matrix?: ColorMatrix
  overlays: readonly Overlay[]
  // Native has no pixel access without a new module, so it bakes overlay-only looks;
  // null means the look needs the matrix and is web-only.
  native: readonly Overlay[] | null
}

const IDENTITY: ColorMatrix = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0]

export function multiplyMatrix(a: ColorMatrix, b: ColorMatrix): number[] {
  const out: number[] = Array.from({ length: 20 }, () => 0)
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 5; c++) {
      let v = c === 4 ? a[r * 5 + 4] : 0
      for (let k = 0; k < 4; k++) v += a[r * 5 + k] * b[k * 5 + c]
      out[r * 5 + c] = v
    }
  }
  return out
}

export function saturateMatrix(s: number): number[] {
  const lr = 0.2126 * (1 - s)
  const lg = 0.7152 * (1 - s)
  const lb = 0.0722 * (1 - s)
  return [lr + s, lg, lb, 0, 0, lr, lg + s, lb, 0, 0, lr, lg, lb + s, 0, 0, 0, 0, 0, 1, 0]
}

export function contrastMatrix(c: number, lift = 0): number[] {
  const o = (1 - c) / 2 + lift
  return [c, 0, 0, 0, o, 0, c, 0, 0, o, 0, 0, c, 0, o, 0, 0, 0, 1, 0]
}

export function scaleMatrix(r: number, g: number, b: number, offset = 0): number[] {
  return [r, 0, 0, 0, offset, 0, g, 0, 0, offset, 0, 0, b, 0, offset, 0, 0, 0, 1, 0]
}

const chain = (...ms: ColorMatrix[]) =>
  ms.reduce<ColorMatrix>((a, b) => multiplyMatrix(b, a), IDENTITY)

export const PHOTO_FILTERS: readonly PhotoFilter[] = [
  { id: 'original', overlays: [], native: [] },
  {
    id: 'warm',
    matrix: chain(scaleMatrix(1.08, 1.01, 0.86, 0.01), saturateMatrix(1.1)),
    overlays: [{ kind: 'fill', color: '#ff9a3c', opacity: 0.06 }],
    native: [
      { kind: 'fill', color: '#ff9a3c', opacity: 0.16 },
      { kind: 'linear', from: '#ffd27a', to: '#ff7a3c', opacity: 0.08 },
    ],
  },
  {
    id: 'cool',
    matrix: chain(scaleMatrix(0.9, 1, 1.1, 0.01), saturateMatrix(0.95)),
    overlays: [{ kind: 'fill', color: '#3c8cff', opacity: 0.06 }],
    native: [
      { kind: 'fill', color: '#3c8cff', opacity: 0.15 },
      { kind: 'linear', from: '#9fd8ff', to: '#2a4fd6', opacity: 0.08 },
    ],
  },
  {
    id: 'mono',
    matrix: chain(saturateMatrix(0), contrastMatrix(1.12)),
    overlays: [],
    native: null,
  },
  {
    id: 'vivid',
    matrix: chain(saturateMatrix(1.45), contrastMatrix(1.08)),
    overlays: [],
    native: null,
  },
  {
    id: 'fade',
    matrix: chain(saturateMatrix(0.8), contrastMatrix(0.84, 0.06)),
    overlays: [{ kind: 'fill', color: '#ffffff', opacity: 0.05 }],
    native: [
      { kind: 'fill', color: '#f4efe6', opacity: 0.22 },
      { kind: 'linear', from: '#ffffff', to: '#d8d0c4', opacity: 0.08 },
    ],
  },
  {
    id: 'film',
    matrix: chain(saturateMatrix(0.88), scaleMatrix(1.04, 1, 0.92), contrastMatrix(0.94, 0.03)),
    overlays: [
      { kind: 'fill', color: '#f5d6a0', opacity: 0.07 },
      { kind: 'vignette', color: '#000000', opacity: 0.32 },
    ],
    native: [
      { kind: 'fill', color: '#e9c98f', opacity: 0.16 },
      { kind: 'vignette', color: '#000000', opacity: 0.4 },
    ],
  },
  {
    id: 'dusk',
    matrix: contrastMatrix(1.05),
    overlays: [{ kind: 'linear', from: '#ff6a88', to: '#2b1a5a', opacity: 0.2 }],
    native: [
      { kind: 'linear', from: '#ff6a88', to: '#2b1a5a', opacity: 0.3 },
      { kind: 'vignette', color: '#120a24', opacity: 0.25 },
    ],
  },
]

export const NATIVE_PHOTO_FILTERS = PHOTO_FILTERS.filter((f) => f.native !== null)

export const photoFilter = (id: string | null | undefined): PhotoFilter =>
  PHOTO_FILTERS.find((f) => f.id === id) ?? PHOTO_FILTERS[0]

export const isPhotoFilterId = (id: unknown): id is PhotoFilterId =>
  typeof id === 'string' && PHOTO_FILTERS.some((f) => f.id === id)

/** Applies a colour matrix to RGBA pixels in place. */
export function applyColorMatrix(px: Uint8ClampedArray, m: ColorMatrix) {
  const o0 = m[4] * 255
  const o1 = m[9] * 255
  const o2 = m[14] * 255
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i]
    const g = px[i + 1]
    const b = px[i + 2]
    px[i] = m[0] * r + m[1] * g + m[2] * b + o0
    px[i + 1] = m[5] * r + m[6] * g + m[7] * b + o1
    px[i + 2] = m[10] * r + m[11] * g + m[12] * b + o2
  }
}

// ─── Geometry ────────────────────────────────────────────────────────────────

export type Rect = { x: number; y: number; w: number; h: number }

/** The centred region of a w×h image that covers a frame of `aspect` (width / height). */
export function coverCrop(w: number, h: number, aspect: number): Rect {
  if (w / h > aspect) {
    const cw = h * aspect
    return { x: (w - cw) / 2, y: 0, w: cw, h }
  }
  const ch = w / aspect
  return { x: 0, y: (h - ch) / 2, w, h: ch }
}

/** Output size for a w×h image scaled to at most `max` px on its long side. */
export function fitWithin(w: number, h: number, max: number) {
  const k = Math.min(1, max / Math.max(w, h))
  return { w: Math.round(w * k), h: Math.round(h * k), k }
}

export const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

// ─── Stickers ────────────────────────────────────────────────────────────────

export type StickerKind = 'habit' | 'streak' | 'dot' | 'time' | 'amount'
export type StickerTone = 'light' | 'dark'
export type StickerSize = 1 | 1.5

export type StickerSpec = {
  kind: StickerKind
  text: string
  color: string
  // The dot sticker's emoji (the habit's emoji or smiley dot style); none draws a check.
  glyph?: string
}

/** A sticker on the photo; x and y are its centre as fractions of the visible frame. */
export type PlacedSticker = {
  kind: StickerKind
  x: number
  y: number
  tone: StickerTone
  size: StickerSize
}

export type PhotoEdit = { filter: PhotoFilterId; stickers: PlacedSticker[] }

export const NO_EDIT: PhotoEdit = { filter: 'original', stickers: [] }

export const isEdited = (e: PhotoEdit) => e.filter !== 'original' || e.stickers.length > 0

export const STREAK_FLAME = { light: '#ea580c', dark: '#ff6b35' } as const

// Lucide's flame, drawn at 24×24 with a 2.25 stroke like the app's StreakFlame.
export const FLAME_PATH =
  'M12 3q1 4 4 6.5t3 5.5a1 1 0 0 1-14 0 5 5 0 0 1 1-3 1 1 0 0 0 5 0c0-2-1.5-3-1.5-5q0-2 2.5-4'
export const CHECK_PATH = 'M20 6 9 17l-5-5'

/** Sticker sizes for a frame whose short side is `short` px. */
export function stickerMetrics(short: number, size: StickerSize) {
  const font = short * 0.05 * size
  return {
    font,
    height: font * 2,
    padX: font * 0.8,
    gap: font * 0.4,
    icon: font * 1.15,
    dot: font * 2.6,
  }
}

const SLOTS: readonly [number, number][] = [
  [0.5, 0.84],
  [0.5, 0.72],
  [0.5, 0.16],
  [0.5, 0.6],
  [0.5, 0.28],
]

/** Where a newly added sticker lands: the first slot not near one already placed. */
export function nextStickerSlot(placed: readonly PlacedSticker[]): [number, number] {
  const free = SLOTS.find(([x, y]) =>
    placed.every((p) => Math.abs(p.x - x) > 0.2 || Math.abs(p.y - y) > 0.06),
  )
  return free ?? [0.5, 0.5]
}

export function toggleSticker(edit: PhotoEdit, kind: StickerKind): PhotoEdit {
  if (edit.stickers.some((s) => s.kind === kind))
    return { ...edit, stickers: edit.stickers.filter((s) => s.kind !== kind) }
  const [x, y] = nextStickerSlot(edit.stickers)
  return { ...edit, stickers: [...edit.stickers, { kind, x, y, tone: 'dark', size: 1 }] }
}

export function updateSticker(
  edit: PhotoEdit,
  kind: StickerKind,
  patch: Partial<Omit<PlacedSticker, 'kind'>>,
): PhotoEdit {
  return {
    ...edit,
    stickers: edit.stickers.map((s) =>
      s.kind === kind
        ? {
            ...s,
            ...patch,
            ...(patch.x !== undefined && { x: clamp01(patch.x) }),
            ...(patch.y !== undefined && { y: clamp01(patch.y) }),
          }
        : s,
    ),
  }
}

/** What each sticker's pill colours are for a tone. */
export function stickerColors(spec: StickerSpec, tone: StickerTone) {
  const dark = tone === 'dark'
  if (spec.kind === 'habit')
    return dark
      ? { bg: 'rgba(11,12,11,0.72)', fg: '#ffffff', accent: spec.color }
      : { bg: spec.color, fg: '#0b0c0b', accent: '#0b0c0b' }
  return dark
    ? {
        bg: 'rgba(11,12,11,0.72)',
        fg: '#ffffff',
        accent: spec.kind === 'streak' ? STREAK_FLAME.dark : spec.color,
      }
    : {
        bg: 'rgba(255,255,255,0.92)',
        fg: '#0b0c0b',
        accent: spec.kind === 'streak' ? STREAK_FLAME.light : spec.color,
      }
}

export type StickerSource = {
  title: string
  color: string
  streak: number
  // A number habit's amount on the photo's day, already rounded by the caller.
  amount?: { value: number; unit: string } | null
  // The habit's emoji dot style, if any.
  glyph?: string | null
  // When the photo is for: today shows the time too, an earlier day only the date.
  when: { ms: number; today: boolean }
  locale: string
  formatAmount: (n: number, locale?: string) => string
}

export function buildStickerSpecs(src: StickerSource): StickerSpec[] {
  const name = src.title.split('///')[0].trim()
  const date = new Intl.DateTimeFormat(src.locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(src.when.ms)
  const time = new Intl.DateTimeFormat(src.locale, { hour: 'numeric', minute: '2-digit' }).format(
    src.when.ms,
  )
  const out: StickerSpec[] = []
  if (name) out.push({ kind: 'habit', text: name, color: src.color })
  if (src.streak > 0) out.push({ kind: 'streak', text: String(src.streak), color: src.color })
  out.push({ kind: 'dot', text: '', color: src.color, ...(src.glyph ? { glyph: src.glyph } : {}) })
  out.push({
    kind: 'time',
    text: src.when.today ? `${date} · ${time}` : date,
    color: src.color,
  })
  if (src.amount && src.amount.value > 0)
    out.push({
      kind: 'amount',
      text: `${src.formatAmount(src.amount.value, src.locale)} ${src.amount.unit}`.trim(),
      color: src.color,
    })
  return out
}
